/**
 * Mock data for Episode of Care Lifecycle Tracker (episode-centric).
 * Used for UI feedback and development without backend.
 */

import type {
  CarePeriodRecord,
  ConsultationRecord,
  EpisodeRow,
  LifecycleStage,
  MatchHistoryEntry,
  PartialSubmission,
  TimelineEvent,
  VerificationErrorRow,
  ClientSummary,
  CoverageSummary,
  MatchScoreRow,
} from '../types/lifecycle';

export const mockPartialSubmissions: PartialSubmission[] = [
  { id: 'ps1', clientName: 'Alex Rivera', email: 'alex.r@example.com', lastStepReached: 3, lastActivity: '2025-03-08T14:00:00Z', practice: 'Wellness Practice', submissionDate: '2025-03-05' },
  { id: 'ps2', clientName: 'Jordan Kim', email: 'jordan.k@example.com', lastStepReached: 1, lastActivity: '2025-03-07T09:30:00Z', submissionDate: '2025-03-04' },
  { id: 'ps3', clientName: 'Sam Taylor', email: 'sam.t@example.com', lastStepReached: 5, lastActivity: '2025-03-09T08:00:00Z', practice: 'Mindful Care', submissionDate: '2025-03-06' },
];

export const mockVerificationErrors: VerificationErrorRow[] = [
  {
    id: 'vob1',
    clientId: 'c1',
    clientName: 'Maria Santos',
    serviceTypeCodes: ['PSY'],
    payer: 'Aetna',
    memberId: 'WXY123456',
    soharErrorReason: 'Member ID not found',
    assumedCopay: '$30',
    soonestBookedAppointment: '2025-03-10T10:00:00Z',
    daysUntilAppointment: 1,
    isUrgent: true,
    submissionDate: '2025-03-02',
  },
  {
    id: 'vob2',
    clientId: 'c2',
    clientName: 'James Chen',
    serviceTypeCodes: ['IOP'],
    payer: 'Blue Cross',
    memberId: 'BC789',
    soharErrorReason: 'Eligibility service timeout',
    assumedCopay: '$50',
    soonestBookedAppointment: '2025-03-15T14:00:00Z',
    daysUntilAppointment: 6,
    submissionDate: '2025-03-03',
  },
];

/** Reusable match history presets so every service request has realistic matching data. */
const MATCH_HISTORY_PRESETS: Record<string, MatchHistoryEntry[]> = {
  /** Currently in matching: 2 offered, 1 declined */
  matchingA: [
    { id: 'mh1', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 92, matchedAt: '2025-03-10T09:00:00Z', status: 'offered' },
    { id: 'mh2', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 85, matchedAt: '2025-03-09T14:00:00Z', status: 'declined', note: 'Client preferred weekend availability' },
    { id: 'mh3', providerId: 'p3', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', score: 78, matchedAt: '2025-03-08T11:00:00Z', status: 'expired' },
  ],
  /** In matching: 3 offered */
  matchingB: [
    { id: 'mh4', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 88, matchedAt: '2025-03-11T10:00:00Z', status: 'offered' },
    { id: 'mh5', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 82, matchedAt: '2025-03-10T08:00:00Z', status: 'offered' },
    { id: 'mh6', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 79, matchedAt: '2025-03-09T16:00:00Z', status: 'withdrawn', note: 'No longer accepting IOP' },
  ],
  /** Consultation: one accepted for consult */
  consultationA: [
    { id: 'mh7', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 94, matchedAt: '2025-03-12T09:00:00Z', status: 'accepted', note: 'Consultation scheduled' },
    { id: 'mh8', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 87, matchedAt: '2025-03-11T14:00:00Z', status: 'declined' },
    { id: 'mh9', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 81, matchedAt: '2025-03-10T10:00:00Z', status: 'expired' },
  ],
  consultationB: [
    { id: 'mh10', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 90, matchedAt: '2025-03-08T11:00:00Z', status: 'accepted', note: 'Consultation completed; first session booked' },
    { id: 'mh11', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 85, matchedAt: '2025-03-07T09:00:00Z', status: 'declined' },
  ],
  /** Active: accepted provider */
  activeA: [
    { id: 'mh12', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 91, matchedAt: '2025-02-01T10:00:00Z', status: 'accepted' },
    { id: 'mh13', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 88, matchedAt: '2025-01-28T14:00:00Z', status: 'declined' },
    { id: 'mh14', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 84, matchedAt: '2025-01-27T09:00:00Z', status: 'expired' },
  ],
  activeB: [
    { id: 'mh15', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 89, matchedAt: '2025-01-15T11:00:00Z', status: 'accepted' },
    { id: 'mh16', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 86, matchedAt: '2025-01-14T08:00:00Z', status: 'declined' },
  ],
  activeC: [
    { id: 'mh17', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 93, matchedAt: '2025-03-01T09:00:00Z', status: 'accepted' },
    { id: 'mh18', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 82, matchedAt: '2025-02-28T14:00:00Z', status: 'offered' },
  ],
  /** Screening: one early match (expired) or empty */
  screeningA: [
    { id: 'mh19', providerId: 'p3', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', score: 76, matchedAt: '2025-03-05T10:00:00Z', status: 'expired', note: 'Pre-screening only' },
  ],
  screeningB: [
    { id: 'mh20', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 88, matchedAt: '2025-03-09T14:00:00Z', status: 'offered' },
  ],
  /** Transfer / inactive / fulfilled / cancelled */
  transferA: [
    { id: 'mh21', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 90, matchedAt: '2025-02-15T10:00:00Z', status: 'accepted' },
    { id: 'mh22', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 91, matchedAt: '2025-03-07T09:00:00Z', status: 'accepted', note: 'Transfer to' },
  ],
  inactiveA: [
    { id: 'mh23', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 89, matchedAt: '2025-01-10T10:00:00Z', status: 'accepted' },
    { id: 'mh24', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 85, matchedAt: '2025-01-09T14:00:00Z', status: 'declined' },
  ],
  inactiveB: [
    { id: 'mh25', providerId: 'p3', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', score: 72, matchedAt: '2025-02-25T11:00:00Z', status: 'declined', note: 'Not a fit after screening' },
  ],
  fulfilledA: [
    { id: 'mh26', providerId: 'p4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', score: 92, matchedAt: '2025-03-10T09:00:00Z', status: 'accepted' },
    { id: 'mh27', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 88, matchedAt: '2025-03-09T10:00:00Z', status: 'declined' },
  ],
  fulfilledB: [
    { id: 'mh28', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 87, matchedAt: '2025-03-08T14:00:00Z', status: 'accepted' },
  ],
  cancelledA: [
    { id: 'mh29', providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 85, matchedAt: '2025-03-01T10:00:00Z', status: 'withdrawn', note: 'Client withdrew' },
  ],
  cancelledB: [
    { id: 'mh30', providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 80, matchedAt: '2025-02-28T09:00:00Z', status: 'expired' },
  ],
  cancelledC: [] as MatchHistoryEntry[],
};

/** Consultations and care periods for Matching tab (chronological timeline). */
const CONSULTATION_PRESETS: Record<string, ConsultationRecord[]> = {
  screening: [
    { id: 'con1', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-03-11T10:00:00Z', outcome: 'Pending — screening scheduled' },
  ],
  screening2: [
    { id: 'con2', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-03-09T14:00:00Z', outcome: 'Good fit; referred to matching' },
  ],
  matching: [
    { id: 'con3', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-03-09T14:00:00Z', outcome: 'Good fit for CBT; referred to matching' },
  ],
  consultation: [
    { id: 'con4', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', date: '2025-03-12T11:00:00Z', outcome: 'High-risk consult; good fit, matched to provider' },
    { id: 'con5', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-03-09T14:00:00Z', outcome: 'Good fit' },
  ],
  consultation2: [
    { id: 'con6', providerName: 'Dr. John Park', practice: 'Mindful Care', date: '2025-03-08T09:00:00Z', outcome: 'Consultation completed; first session booked' },
    { id: 'con7', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-03-05T10:00:00Z', outcome: 'Good fit' },
  ],
  active: [
    { id: 'con8', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', date: '2025-01-28T14:00:00Z', outcome: 'Good fit, matched to regular provider' },
  ],
  transfer: [
    { id: 'con9', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', date: '2025-03-07T09:00:00Z', outcome: 'Accepting transfer; care to begin after handoff' },
    { id: 'con10', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-02-15T10:00:00Z', outcome: 'Good fit, matched' },
  ],
  inactive: [
    { id: 'con11', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', date: '2025-02-25T11:00:00Z', outcome: 'Not a fit after screening' },
  ],
  fulfilled: [
    { id: 'con12', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', date: '2025-03-09T10:00:00Z', outcome: 'Good fit; first session booked' },
  ],
  cancelled: [
    { id: 'con13', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', date: '2025-03-01T10:00:00Z', outcome: 'Client withdrew before starting' },
  ],
  empty: [] as ConsultationRecord[],
};

const CARE_PERIOD_PRESETS: Record<string, CarePeriodRecord[]> = {
  none: [],
  active: [
    { id: 'care1', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', startDate: '2025-02-01', endDate: null },
  ],
  active2: [
    { id: 'care2', providerName: 'Dr. John Park', practice: 'Mindful Care', startDate: '2025-01-15', endDate: null },
  ],
  active3: [
    { id: 'care3', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', startDate: '2025-03-01', endDate: null },
  ],
  transfer: [
    { id: 'care4', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', startDate: '2025-02-15', endDate: '2025-03-07' },
    { id: 'care5', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', startDate: '2025-03-08', endDate: null },
  ],
  inactive: [
    { id: 'care6', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', startDate: '2025-01-10', endDate: '2025-02-28' },
  ],
  inactive2: [
    { id: 'care7', providerName: 'Dr. Amy Screen', practice: 'Wellness Practice', startDate: '2025-02-20', endDate: '2025-03-01' },
  ],
  fulfilled: [
    { id: 'care8', providerName: 'Dr. Sarah Miller', practice: 'Wellness Practice', startDate: '2025-03-10', endDate: null },
  ],
  fulfilled2: [
    { id: 'care9', providerName: 'Dr. John Park', practice: 'Mindful Care', startDate: '2025-03-08', endDate: null },
  ],
};

/** Default values for every possible table column so no cell is blank in mock data. */
const DEFAULT_EPISODE_FIELDS: Record<string, unknown> = {
  submissionDate: '2025-03-01',
  email: 'client@example.com',
  lastStepReached: 3,
  lastActivity: '2025-03-08T14:00:00Z',
  serviceTypeCodes: ['PSY'],
  memberId: 'MEM001',
  soharErrorReason: 'None',
  assumedCopay: '$25',
  soonestBookedAppointment: '2025-03-15T10:00:00Z',
  daysUntilAppointment: 7,
  waitlistReason: 'no-provider',
  daysOnWaitlist: 0,
  waitlistNotes: '—',
  flaggedQuestions: ['—'],
  screeningProvider: 'Dr. Amy Screen',
  screeningAppointmentDate: '2025-03-12T10:00:00Z',
  meetingStatus: 'upcoming' as const,
  screeningOutcome: 'pending' as const,
  preferencesSummary: 'Flexible',
  daysWaiting: 0,
  consultationProvider: 'Dr. Jane Doe',
  consultationDate: '2025-03-10T11:00:00Z',
  assignedProvider: '—',
  startDate: '2025-03-01',
  lastAppointment: '2025-03-08',
  verificationStatus: 'verified' as const,
  fromProvider: '—',
  toProvider: '—',
  transferReason: '—',
  transferRequestedAt: '2025-03-01T09:00:00Z',
  transferStatus: '—',
  lastProvider: '—',
  deactivationDate: '—',
  deactivationReason: '—',
  cancelledDate: '—',
  cancelReason: '—',
  fulfilledDate: '—',
  fulfilledStatus: 'ongoing' as const,
};

const baseEpisodes: Omit<EpisodeRow, 'stage'>[] = [
  { id: 'e1', clientId: 'c1', clientName: 'Maria Santos', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c1-individual', practice: 'Wellness Practice', payer: 'Aetna', state: 'CA', email: 'maria.s@example.com', memberId: 'WXY123456', serviceTypeCodes: ['PSY'], assumedCopay: '$30' },
  { id: 'e2', clientId: 'c2', clientName: 'James Chen', episodeServiceType: 'IOP', episodeOfCareId: 'eoc-c2-iop', practice: 'Mindful Care', payer: 'Blue Cross', state: 'CA', email: 'james.c@example.com', memberId: 'BC789', serviceTypeCodes: ['IOP'], assumedCopay: '$50' },
  { id: 'e3', clientId: 'c3', clientName: 'Riley Johnson', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c3-individual', practice: 'Wellness Practice', payer: 'Cigna', state: 'NY', email: 'riley.j@example.com', memberId: 'CG111', serviceTypeCodes: ['PSY'], assumedCopay: '$20' },
  { id: 'e4', clientId: 'c4', clientName: 'Casey Lee', episodeServiceType: 'Couples Therapy', episodeOfCareId: 'eoc-c4-couples', practice: 'Mindful Care', payer: 'Aetna', state: 'CA', email: 'casey.l@example.com', memberId: 'AT222', serviceTypeCodes: ['PSY'], assumedCopay: '$35' },
  { id: 'e5', clientId: 'c5', clientName: 'Morgan Davis', episodeServiceType: 'Group Therapy', episodeOfCareId: 'eoc-c5-group', practice: 'Wellness Practice', payer: 'United', state: 'TX', email: 'morgan.d@example.com', memberId: 'UN333', serviceTypeCodes: ['PSY'], assumedCopay: '$40' },
  { id: 'e6', clientId: 'c6', clientName: 'Taylor Brown', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c6-individual', practice: 'Mindful Care', payer: 'Cigna', state: 'NY', email: 'taylor.b@example.com', memberId: 'CG444', serviceTypeCodes: ['PSY'], assumedCopay: '$25' },
  { id: 'e7', clientId: 'c7', clientName: 'Jordan Smith', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c7-individual', practice: 'Wellness Practice', payer: 'Aetna', state: 'CA', assignedProvider: 'Dr. Sarah Miller', startDate: '2025-02-01', lastAppointment: '2025-03-05', verificationStatus: 'verified', email: 'jordan.s@example.com', memberId: 'AT555', serviceTypeCodes: ['PSY'], assumedCopay: '$30' },
  { id: 'e8', clientId: 'c8', clientName: 'Alex Turner', episodeServiceType: 'IOP', episodeOfCareId: 'eoc-c8-iop', practice: 'Mindful Care', payer: 'Blue Cross', state: 'CA', assignedProvider: 'Dr. John Park', startDate: '2025-01-15', lastAppointment: '2025-03-08', verificationStatus: 'verified', email: 'alex.t@example.com', memberId: 'BC666', serviceTypeCodes: ['IOP'], assumedCopay: '$50' },
  { id: 'e9', clientId: 'c9', clientName: 'Sam Wilson', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c9-individual', practice: 'Wellness Practice', payer: 'Aetna', state: 'CA', fromProvider: 'Dr. Sarah Miller', toProvider: 'Dr. Jane Doe', transferReason: 'Client relocation', transferRequestedAt: '2025-03-07T09:00:00Z', transferStatus: 'pending', email: 'sam.w@example.com', memberId: 'AT777', serviceTypeCodes: ['PSY'], assumedCopay: '$30' },
  { id: 'e10', clientId: 'c10', clientName: 'Jamie Fox', episodeServiceType: 'Individual Therapy', episodeOfCareId: 'eoc-c10-individual', practice: 'Wellness Practice', payer: 'Cigna', state: 'NY', lastProvider: 'Dr. Sarah Miller', deactivationDate: '2025-02-28', deactivationReason: 'Completed treatment', email: 'jamie.f@example.com', memberId: 'CG888', serviceTypeCodes: ['PSY'], assumedCopay: '$20' },
];

let episodeIdx = 0;
function episode(stage: LifecycleStage, overrides: Partial<EpisodeRow> = {}): EpisodeRow {
  const base = baseEpisodes[episodeIdx++ % baseEpisodes.length];
  return { ...DEFAULT_EPISODE_FIELDS, ...base, id: `${base.id}-${stage}-${episodeIdx}`, stage, ...overrides } as EpisodeRow;
}

export function getMockEpisodesByStage(stage: LifecycleStage): EpisodeRow[] {
  switch (stage) {
    case 'cancelled':
      return [
        episode('cancelled', { cancelledDate: '2025-03-01', cancelReason: 'found-elsewhere', practice: 'Wellness Practice', matchHistory: MATCH_HISTORY_PRESETS.cancelledA, consultations: CONSULTATION_PRESETS.cancelled, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('cancelled', { cancelledDate: '2025-02-28', cancelReason: 'unresponsive', practice: 'Mindful Care', matchHistory: MATCH_HISTORY_PRESETS.cancelledB, consultations: CONSULTATION_PRESETS.empty, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('cancelled', { cancelledDate: '2025-03-05', cancelReason: 'no-longer-interested', practice: 'Wellness Practice', matchHistory: MATCH_HISTORY_PRESETS.cancelledC, consultations: CONSULTATION_PRESETS.empty, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('cancelled', { cancelledDate: '2025-03-09', cancelReason: 'referred-out', practice: 'Mindful Care', matchHistory: MATCH_HISTORY_PRESETS.cancelledC, consultations: CONSULTATION_PRESETS.empty, carePeriods: CARE_PERIOD_PRESETS.none }),
      ];
    case 'fulfilled':
      return [
        episode('fulfilled', { assignedProvider: 'Dr. Sarah Miller', fulfilledDate: '2025-03-10', fulfilledStatus: 'ongoing', practice: 'Wellness Practice', matchHistory: MATCH_HISTORY_PRESETS.fulfilledA, consultations: CONSULTATION_PRESETS.fulfilled, carePeriods: CARE_PERIOD_PRESETS.fulfilled }),
        episode('fulfilled', { assignedProvider: 'Dr. John Park', fulfilledDate: '2025-03-08', fulfilledStatus: 'completed', practice: 'Mindful Care', matchHistory: MATCH_HISTORY_PRESETS.fulfilledB, consultations: CONSULTATION_PRESETS.fulfilled, carePeriods: CARE_PERIOD_PRESETS.fulfilled2 }),
      ];
    case 'screening':
      return [
        episode('screening', { screeningYesCount: 2, flaggedQuestions: ['Crisis', 'Medications'], screeningProvider: 'Dr. Amy Screen', screeningAppointmentDate: '2025-03-11T10:00:00Z', meetingStatus: 'upcoming', screeningOutcome: 'pending', matchHistory: MATCH_HISTORY_PRESETS.screeningA, consultations: CONSULTATION_PRESETS.screening, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('screening', { screeningYesCount: 3, flaggedQuestions: ['Crisis', 'Suicidal ideation', 'Hospitalization'], screeningProvider: 'Dr. Amy Screen', screeningAppointmentDate: '2025-03-09T14:00:00Z', meetingStatus: 'completed', screeningOutcome: 'good-fit', matchHistory: MATCH_HISTORY_PRESETS.screeningB, consultations: CONSULTATION_PRESETS.screening2, carePeriods: CARE_PERIOD_PRESETS.none }),
      ];
    case 'matching':
      return [
        episode('matching', { preferencesSummary: 'Weekday PM, telehealth OK', screeningOutcomeNotes: 'Good fit for CBT', daysWaiting: 2, matchSource: 'screening', matchScore: 92, matchHistory: MATCH_HISTORY_PRESETS.matchingA, consultations: CONSULTATION_PRESETS.matching, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('matching', { preferencesSummary: 'In-person preferred', daysWaiting: 5, matchSource: 'rematch-request', matchScore: 88, matchHistory: MATCH_HISTORY_PRESETS.matchingB, consultations: CONSULTATION_PRESETS.matching, carePeriods: CARE_PERIOD_PRESETS.none }),
      ];
    case 'consultation':
      return [
        episode('consultation', { consultationProvider: 'Dr. Jane Doe', consultationDate: '2025-03-12T11:00:00Z', consultationSource: 'high-risk', meetingStatus: 'upcoming', screeningOutcome: 'pending', firstSessionBooked: false, matchHistory: MATCH_HISTORY_PRESETS.consultationA, consultations: CONSULTATION_PRESETS.consultation, carePeriods: CARE_PERIOD_PRESETS.none }),
        episode('consultation', { consultationProvider: 'Dr. John Park', consultationDate: '2025-03-08T09:00:00Z', consultationSource: 'practice-policy', meetingStatus: 'completed', screeningOutcome: 'good-fit', firstSessionBooked: true, matchHistory: MATCH_HISTORY_PRESETS.consultationB, consultations: CONSULTATION_PRESETS.consultation2, carePeriods: CARE_PERIOD_PRESETS.none }),
      ];
    case 'active':
      return [
        episode('active', { assignedProvider: 'Dr. Sarah Miller', startDate: '2025-02-01', lastAppointment: '2025-03-05', verificationStatus: 'verified', matchHistory: MATCH_HISTORY_PRESETS.activeA, consultations: CONSULTATION_PRESETS.active, carePeriods: CARE_PERIOD_PRESETS.active }),
        episode('active', { assignedProvider: 'Dr. John Park', startDate: '2025-01-15', lastAppointment: '2025-03-08', verificationStatus: 'verified', matchHistory: MATCH_HISTORY_PRESETS.activeB, consultations: CONSULTATION_PRESETS.active, carePeriods: CARE_PERIOD_PRESETS.active2 }),
        episode('active', { assignedProvider: 'Dr. Jane Doe', startDate: '2025-03-01', lastAppointment: '2025-03-06', verificationStatus: 'needs-verification', matchHistory: MATCH_HISTORY_PRESETS.activeC, consultations: CONSULTATION_PRESETS.active, carePeriods: CARE_PERIOD_PRESETS.active3 }),
      ];
    case 'transfer':
      return [
        episode('transfer', { fromProvider: 'Dr. Sarah Miller', toProvider: 'Dr. Jane Doe', transferReason: 'Client relocation', transferRequestedAt: '2025-03-07T09:00:00Z', transferStatus: 'pending', matchHistory: MATCH_HISTORY_PRESETS.transferA, consultations: CONSULTATION_PRESETS.transfer, carePeriods: CARE_PERIOD_PRESETS.transfer }),
      ];
    case 'inactive':
      return [
        episode('inactive', { lastProvider: 'Dr. Sarah Miller', deactivationDate: '2025-02-28', deactivationReason: 'Completed treatment', matchHistory: MATCH_HISTORY_PRESETS.inactiveA, consultations: CONSULTATION_PRESETS.active, carePeriods: CARE_PERIOD_PRESETS.inactive }),
        episode('inactive', { lastProvider: 'Dr. Amy Screen', deactivationDate: '2025-03-01', deactivationReason: 'Not a fit after screening', matchHistory: MATCH_HISTORY_PRESETS.inactiveB, consultations: CONSULTATION_PRESETS.inactive, carePeriods: CARE_PERIOD_PRESETS.inactive2 }),
      ];
    case 'all':
      return [
        ...getMockEpisodesByStage('screening'),
        ...getMockEpisodesByStage('matching'),
        ...getMockEpisodesByStage('consultation'),
        ...getMockEpisodesByStage('active'),
        ...getMockEpisodesByStage('transfer'),
        ...getMockEpisodesByStage('inactive'),
        ...getMockEpisodesByStage('cancelled'),
        ...getMockEpisodesByStage('fulfilled'),
      ];
    default:
      return [];
  }
}

export const mockTimelineEvents: TimelineEvent[] = [
  { id: 'te1', timestamp: '2025-03-09T10:00:00Z', actor: 'System', action: 'Episode moved to Active', outcomeDesc: 'First session booked', entityType: 'episode' },
  { id: 'te2', timestamp: '2025-03-08T15:30:00Z', actor: 'Admin (you)', action: 'Manual verification override', outcomeDesc: 'Benefits entered for PSY', entityType: 'vob' },
  { id: 'te3', timestamp: '2025-03-08T14:00:00Z', actor: 'Dr. Jane Doe', action: 'Consultation completed', outcomeDesc: 'Good fit, matched to regular provider', entityType: 'episode' },
  { id: 'te4', timestamp: '2025-03-07T09:00:00Z', actor: 'Dr. Sarah Miller', action: 'Transfer requested', outcomeDesc: 'Client relocation', entityType: 'episode' },
];

export const mockClientSummary: ClientSummary = {
  id: 'c1',
  name: 'Maria Santos',
  pronouns: 'she/her',
  email: 'maria.s@example.com',
  phone: '(555) 123-4567',
  isHighRisk: false,
  episodes: [
    getMockEpisodesByStage('active')[0],
    getMockEpisodesByStage('screening')[0],
  ].map((e) => ({ ...e, clientId: 'c1', clientName: 'Maria Santos' })),
};

export const mockCoverages: CoverageSummary[] = [
  { id: 'cov1', payer: 'Aetna', memberId: 'WXY123456', serviceTypeCode: 'PSY', status: 'error', copay: '$30', lastVerified: undefined, paymentType: 'insurance' },
  { id: 'cov2', payer: 'Aetna', memberId: 'WXY123456', serviceTypeCode: 'IOP', status: 'verified', copay: '$30', lastVerified: '2025-02-15', paymentType: 'insurance' },
  { id: 'cov3', payer: 'Cash pay', serviceTypeCode: 'PSY', status: 'verified', copay: '$150/session', paymentType: 'cash' },
  { id: 'cov4', payer: 'Blue Cross', memberId: 'BC789', serviceTypeCode: 'IOP', status: 'pending', copay: '$50', paymentType: 'insurance' },
];

export const mockMatchScores: MatchScoreRow[] = [
  { providerId: 'p1', providerName: 'Dr. Jane Doe', practice: 'Wellness Practice', score: 92, breakdown: [{ label: 'Availability', value: 95 }, { label: 'Location', value: 90 }, { label: 'Specialties', value: 88 }, { label: 'Insurance', value: 100 }], caseloadCapacity: 'Moderate', languages: ['English', 'Spanish'], specialties: ['Anxiety', 'CBT'], acceptedPayers: ['Aetna', 'Cigna'] },
  { providerId: 'p2', providerName: 'Dr. John Park', practice: 'Mindful Care', score: 85, breakdown: [{ label: 'Availability', value: 80 }, { label: 'Location', value: 90 }, { label: 'Specialties', value: 85 }, { label: 'Insurance', value: 90 }], caseloadCapacity: 'Full', languages: ['English'], specialties: ['Depression', 'IOP'], acceptedPayers: ['Aetna', 'Blue Cross'] },
];

export const mockRecentActivity: TimelineEvent[] = mockTimelineEvents.slice(0, 5);

/** Counts per stage for summary cards (mock). */
export function getMockStageCounts(): Record<LifecycleStage, number> {
  return {
    'new-client': mockPartialSubmissions.length,
    'needs-verification': mockVerificationErrors.length,
    waitlist: getMockEpisodesByStage('waitlist').length,
    screening: getMockEpisodesByStage('screening').length,
    matching: getMockEpisodesByStage('matching').length,
    consultation: getMockEpisodesByStage('consultation').length,
    active: getMockEpisodesByStage('active').length,
    transfer: getMockEpisodesByStage('transfer').length,
    inactive: getMockEpisodesByStage('inactive').length,
    cancelled: getMockEpisodesByStage('cancelled').length,
    fulfilled: getMockEpisodesByStage('fulfilled').length,
    all: getMockEpisodesByStage('all').length,
  };
}
